/**
 * Global fetch interceptor that auto-refreshes the Cognito session.
 *
 * Background: every API call from the openapi-generated client uses
 * plain `fetch(...)` from generated.ts. Cognito caps access/id tokens
 * at 24h but the refresh token lives 30 days. We patch window.fetch
 * once at app boot so that on a 401 response from `/api/*` we:
 *
 *   1. Call POST /api/auth/refresh exactly once (deduped if a parallel
 *      request also 401'd).
 *   2. If refresh succeeds → replay the original request.
 *   3. If refresh fails (refresh token expired) → propagate the 401
 *      and let the SPA redirect to /login.
 *
 * Auth endpoints (/api/auth/login, /api/auth/refresh, /api/auth/logout)
 * are NOT retried — that would loop forever.
 */

const REFRESH_URL = "/api/auth/refresh";
const AUTH_PATHS = new Set([
  "/api/auth/login",
  "/api/auth/refresh",
  "/api/auth/logout",
]);

let refreshPromise: Promise<boolean> | null = null;

function shouldSkip(url: string): boolean {
  try {
    const u = url.startsWith("http") ? new URL(url) : new URL(url, window.location.origin);
    if (!u.pathname.startsWith("/api/")) return true;
    if (AUTH_PATHS.has(u.pathname)) return true;
    return false;
  } catch {
    return true;
  }
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

async function doRefresh(originalFetch: typeof fetch): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const r = await originalFetch(REFRESH_URL, {
          method: "POST",
          credentials: "include",
        });
        return r.status === 200;
      } catch {
        return false;
      } finally {
        // Allow next refresh attempt only after this one resolves so
        // a burst of 401s in the same tick all wait on a single retry.
        setTimeout(() => {
          refreshPromise = null;
        }, 0);
      }
    })();
  }
  return refreshPromise;
}

export function installAuthRefresh(): void {
  if (typeof window === "undefined") return;
  if ((window as unknown as { __aiAuthRefreshInstalled?: boolean }).__aiAuthRefreshInstalled) {
    return;
  }
  const original = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const url = urlOf(input);
    const response = await original(input, init);
    if (response.status !== 401 || shouldSkip(url)) return response;
    const ok = await doRefresh(original);
    if (!ok) return response;
    // Retry once with the new cookies in place.
    return original(input, init);
  };

  (window as unknown as { __aiAuthRefreshInstalled?: boolean }).__aiAuthRefreshInstalled = true;
}
